import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  signal,
  computed,
  inject,
  ViewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  formatCurrencyVND,
  formatDateTime,
} from '@app/shared/utils/date-formatter.util';
import { PermissionService } from '@app/core/services/permission.service';

import {
  SearchFormComponent,
  FormFieldConfig,
} from '@shared/components/search-form/search-form.component';
import { withPaginatedSearchFields } from '@shared/utils/paginated-field.helpers';
import { AdvancedDataTableComponent } from '@shared/components/advanced-data-table/advanced-data-table.component';
import { AppButtonComponent } from '@shared/components/app-button/app-button.component';
import { StatusFilterComponent } from '@app/shared/components/status-filter/status-filter.component';
import type { TableColumn } from '@shared/components/data-table/data-table.component';
import {
  POS_SHIFT_OPEN_COLUMNS,
  POS_SHIFT_OPEN_DEFAULT_COLUMNS_ORDER,
  POS_SHIFT_OPEN_FLEXIBLE_COLUMNS_ORDER,
  POS_SHIFT_OPEN_ACTION_COLUMN,
  getPosShiftOpenSearchConfig,
} from './pos-shift-open.config';
import {
  PosShiftOpenService,
  PosShiftOpening,
  PosShiftOpeningSearchParams,
} from './services/pos-shift-open.service';

@Component({
  selector: 'app-pos-shift-open',
  standalone: true,
  imports: [
    CommonModule,
    TagModule,
    TooltipModule,
    ToastModule,
    TranslateModule,
    SearchFormComponent,
    AdvancedDataTableComponent,
    AppButtonComponent,
    StatusFilterComponent,
  ],
  providers: [MessageService],
  templateUrl: './pos-shift-open.component.html',
  styleUrls: ['./pos-shift-open.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PosShiftOpenComponent implements OnInit {
  // Search configuration
  searchConfig!: FormFieldConfig[];

  // Table configuration (kept for reference, but using advanced table columns below)
  tableColumns = POS_SHIFT_OPEN_COLUMNS;

  private readonly permissionService = inject(PermissionService);
  private readonly permissionCodes = toSignal(
    this.permissionService.userPermissions$,
    {
      initialValue: this.permissionService.getCurrentPermissions(),
    },
  );

  // Permission flags - computed from PermissionService
  // Using specific submenu 'open_pos_shift' for POS permissions
  canRead = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction('pos.open_pos_shift.view');
  });

  // Check if user has any action permissions
  hasAnyActionPermission = computed(() => this.canRead());

  // Advanced table column configuration
  defaultColumns = computed((): TableColumn[] => {
    const fromConfig = (prop: string) =>
      POS_SHIFT_OPEN_COLUMNS.find((c) => c.prop === prop);

    const cols: TableColumn[] = [];
    for (const key of POS_SHIFT_OPEN_DEFAULT_COLUMNS_ORDER) {
      const col = fromConfig(key);
      if (col) cols.push(col);
    }

    // Conditionally add actions column at the end if permitted
    if (this.hasAnyActionPermission()) {
      const actionsCol = fromConfig(POS_SHIFT_OPEN_ACTION_COLUMN.ACTIONS);
      if (actionsCol) cols.push(actionsCol);
    }

    return cols;
  });

  flexibleColumns = computed((): TableColumn[] => {
    const fromConfig = (prop: string) =>
      POS_SHIFT_OPEN_COLUMNS.find((c) => c.prop === prop);

    const cols: TableColumn[] = [];

    for (const prop of POS_SHIFT_OPEN_FLEXIBLE_COLUMNS_ORDER) {
      const col = fromConfig(prop);
      if (col) {
        cols.push(col);
      }
    }

    return cols;
  });

  // Data signals with proper typing
  posShiftOpenings = signal<PosShiftOpening[]>([]);
  loading = signal(false);
  totalRecords = signal(0);

  // Search parameters
  searchParams = signal({
    posId: undefined,
    name: undefined, // cashier name
    startTimeFrom: undefined,
    startTimeTo: undefined,
    status: undefined,
    page: 0,
    size: 20,
  });

  // Selection with proper typing
  selectedPosShiftOpenings = signal<PosShiftOpening[]>([]);

  // Computed properties
  hasSelected = computed(() => this.selectedPosShiftOpenings().length > 0);

  constructor(
    private readonly router: Router,
    private readonly messageService: MessageService,
    private readonly translateService: TranslateService,
    private readonly posShiftOpenService: PosShiftOpenService,
  ) {}

  ngOnInit(): void {
    this.searchConfig = withPaginatedSearchFields(
      getPosShiftOpenSearchConfig(this.posShiftOpenService),
      {
        posId: {
          loadPage: (params) =>
            this.posShiftOpenService.getPosTerminalOptionsPage(params),
        },
      },
    );
    this.loadPosShiftOpenings();
  }

  /**
   * Load POS shift opening records based on search parameters
   */
  loadPosShiftOpenings(): void {
    this.loading.set(true);

    const searchParams: PosShiftOpeningSearchParams = {
      posId: this.searchParams().posId,
      cashierName: this.searchParams().name,
      startTimeFrom: this.searchParams().startTimeFrom,
      startTimeTo: this.searchParams().startTimeTo,
      status: this.searchParams().status,
      page: this.searchParams().page,
      size: this.searchParams().size,
    };

    this.posShiftOpenService.getPosShiftOpenings(searchParams).subscribe({
      next: (response) => {
        this.posShiftOpenings.set(response.data);
        this.totalRecords.set(response.totalRecords);
        this.loading.set(false);
      },
      error: (error) => {
        console.error('Error loading POS shift openings:', error);
        this.loading.set(false);
        this.messageService.add({
          severity: 'error',
          summary: this.translateService.instant('common.messages.error'),
          detail: this.translateService.instant(
            'pos.shift_open.messages.load_failed',
          ),
        });
      },
    });
  }

  /**
   * Handle search form submission
   */
  onSearch(searchData: any): void {
    this.searchParams.set({
      ...this.searchParams(),
      ...searchData,
      page: 0, // Reset to page 0 when searching
      size: this.searchParams().size, // Keep current size
    });
    this.loadPosShiftOpenings();
  }

  /**
   * Handle search form reset
   */
  onReset(): void {
    this.searchParams.set({
      posId: undefined,
      name: undefined,
      startTimeFrom: undefined,
      startTimeTo: undefined,
      status: undefined,
      page: 0,
      size: 20,
    });
    this.selectedPosShiftOpenings.set([]);
    this.loadPosShiftOpenings();

    // Show success message
    this.messageService.add({
      severity: 'info',
      summary: this.translateService.instant('common.messages.info'),
      detail: this.translateService.instant(
        'pos.shift_open.messages.search_reset',
      ),
    });
  }

  /**
   * Handle page change
   */
  onPageChange(event: any): void {
    this.searchParams.set({
      ...this.searchParams(),
      page: event.page,
    });
    this.loadPosShiftOpenings();
  }

  /**
   * Navigate to view POS shift opening details
   */
  onView(shiftOpening: PosShiftOpening): void {
    this.router.navigate(['/pos/shift-open/view', shiftOpening.id]);
  }

  /**
   * Get status icon class
   */
  getStatusIcon(status: string): string {
    switch (status) {
      case 'OPEN':
        return 'pi pi-clock';
      case 'CLOSED':
        return 'pi pi-check-circle';
      case 'CREATE':
        return 'pi pi-exclamation-triangle';
      default:
        return 'pi pi-question-circle';
    }
  }

  /**
   * Get status display text
   */
  getStatusDisplayText(status: string): string {
    return this.translateService.instant(
      `pos.shift_open.status.${status.toLowerCase()}`,
    );
  }

  /**
   * Get status severity
   */
  getStatusSeverity(status: string): string {
    switch (status) {
      case 'OPEN':
        return 'success';
      case 'CLOSED':
        return 'danger';
      case 'CREATE':
        return 'warn';
      default:
        return 'secondary';
    }
  }

  /**
   * Get status style
   */
  getStatusStyle(status: string): any {
    const colorMap: { [key: string]: string } = {
      OPEN: '#3b82f6',
      CLOSED: '#22c55e',
      CREATE: '#f59e0b',
    };

    return {
      '--status-color': colorMap[status] || '#6b7280',
    };
  }

  /**
   * Format datetime using utility function
   */
  formatDateTime = formatDateTime;
  formatCurrency = formatCurrencyVND;

  // Reference to advanced table component
  @ViewChild('advancedTable') advancedTable?: AdvancedDataTableComponent;

  onColumnConfigClick(event: Event): void {
    const target = event.target as HTMLElement;
    // Find the actual button element from PrimeNG p-button
    const pButtonElement = target.closest('p-button');
    const buttonElement = pButtonElement
      ? (pButtonElement.querySelector('button') as HTMLElement)
      : (target.closest('button') as HTMLElement) || target;

    if (buttonElement && this.advancedTable) {
      this.advancedTable.openColumnSelector(buttonElement);
    }
  }
}
